import { LegalLayout, LegalSection } from './LegalLayout';

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Política de Privacidad"
      metaDescription="Política de Privacidad de FinScope: qué información recopilamos, cómo la usamos y protegemos, integraciones con Google y tus derechos como usuario."
      lastUpdated="28 de junio de 2026"
    >
      <p className="text-muted-foreground">
        En FinScope valoramos tu confianza. Esta Política de Privacidad describe qué información recopilamos,
        cómo la utilizamos, cómo la protegemos y qué derechos tienes sobre ella cuando utilizas la plataforma
        FinScope, disponible en app.finscope.mx.
      </p>

      <LegalSection number={1} title="Responsable de la plataforma">
        <p>
          FinScope es una plataforma de software como servicio (SaaS) de gestión y análisis financiero
          empresarial. El tratamiento de la información descrita en esta política es responsabilidad del
          equipo operador de FinScope. Puedes contactarnos mediante los canales de atención indicados en la
          sección de Contacto de esta política.
        </p>
      </LegalSection>

      <LegalSection number={2} title="Qué es FinScope">
        <p>
          FinScope permite a empresas y equipos gestionar y analizar su información financiera: dashboard
          ejecutivo, movimientos, categorías, clientes, flujo de efectivo (cash flow), estado de resultados
          (P&amp;L), facturación, pagos, prioridades y rentabilidad. FinScope no es un banco, institución
          financiera, asesor financiero, contador ni auditor, y no presta servicios financieros regulados.
          La información y visualizaciones que ofrece tienen fines informativos y de gestión interna.
        </p>
      </LegalSection>

      <LegalSection number={3} title="Información que recopilamos">
        <p>Según cómo utilices la plataforma, FinScope puede recopilar:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <span className="font-medium text-foreground">Datos de cuenta y perfil:</span> nombre, correo
            electrónico, rol dentro de la plataforma y organización o empresa a la que perteneces.
          </li>
          <li>
            <span className="font-medium text-foreground">Información proporcionada por el usuario:</span>{' '}
            datos que tú o tu organización capturan o cargan en la plataforma, incluyendo archivos que decidas
            importar.
          </li>
          <li>
            <span className="font-medium text-foreground">Información financiera y de negocio:</span> datos
            relacionados con movimientos, categorías, clientes, flujo de efectivo, estado de resultados,
            facturación, pagos, prioridades y rentabilidad que tu organización gestione dentro de FinScope.
          </li>
          <li>
            <span className="font-medium text-foreground">Información de integraciones autorizadas:</span>{' '}
            datos a los que tú autorices acceso al conectar servicios externos (por ejemplo, Google), conforme
            a la sección 6.
          </li>
          <li>
            <span className="font-medium text-foreground">Información técnica y de seguridad:</span> registros
            necesarios para el funcionamiento, seguridad y auditoría de la plataforma, como registros de
            inicio de sesión, identificadores de sesión y registros de sincronización o errores.
          </li>
        </ul>
      </LegalSection>

      <LegalSection number={4} title="Uso de la información">
        <p>Utilizamos la información únicamente para:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Proporcionar, operar y mantener las funcionalidades de FinScope.</li>
          <li>Generar los reportes, visualizaciones y análisis que la plataforma ofrece a tu organización.</li>
          <li>Autenticar usuarios y mantener la seguridad de las cuentas.</li>
          <li>Ejecutar las integraciones y sincronizaciones que el usuario haya autorizado.</li>
          <li>Diagnosticar errores, prevenir usos indebidos y mejorar el servicio.</li>
        </ul>
        <p>No vendemos tu información personal ni la utilizamos con fines publicitarios.</p>
      </LegalSection>

      <LegalSection number={5} title="Conexiones con servicios externos">
        <p>
          FinScope puede ofrecer integraciones opcionales con servicios de terceros. Estas integraciones solo
          se activan cuando un usuario autorizado de tu organización las conecta de forma explícita. El uso de
          cada servicio externo está sujeto, además, a los términos y políticas de privacidad de dicho
          tercero.
        </p>
      </LegalSection>

      <LegalSection number={6} title="Google OAuth y APIs de Google">
        <p>
          FinScope permite, de forma opcional, conectar una cuenta de Google para utilizar archivos de Google
          Drive y Google Sheets como fuente de datos (por ejemplo, para el módulo de Facturación). Sobre esta
          integración:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            La conexión se realiza mediante Google OAuth 2.0: la autorización ocurre directamente en Google y
            FinScope nunca solicita ni almacena tu contraseña de Google.
          </li>
          <li>
            FinScope solicita únicamente permisos de <span className="font-medium text-foreground">solo lectura</span>{' '}
            sobre Google Drive y Google Sheets, con el fin de listar los archivos accesibles y leer el
            contenido de las hojas de cálculo que el usuario seleccione. FinScope no solicita permisos de
            escritura, edición ni eliminación sobre tus archivos de Google.
          </li>
          <li>
            El acceso queda limitado a los permisos (scopes) que autorices en la pantalla de consentimiento de
            Google y se utiliza exclusivamente para proporcionar la funcionalidad que activaste.
          </li>
          <li>
            Los tokens de acceso otorgados por Google se almacenan cifrados, están asociados exclusivamente a
            la organización que realizó la conexión y nunca se muestran ni se comparten.
          </li>
          <li>
            Puedes revocar el acceso en cualquier momento desde la propia plataforma (opción
            &quot;Desconectar&quot;, que además solicita a Google la revocación del token) o directamente desde
            la configuración de permisos de tu cuenta de Google en{' '}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              myaccount.google.com/permissions
            </a>
            .
          </li>
        </ul>
        <p>
          El uso y la transferencia a cualquier otra aplicación de la información recibida de las APIs de
          Google por parte de FinScope se apegará a la{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Política de Datos de Usuario de los Servicios API de Google
          </a>
          , incluidos los requisitos de Uso Limitado (Limited Use).
        </p>
      </LegalSection>

      <LegalSection number={7} title="Almacenamiento y protección de datos">
        <p>
          La información se almacena en bases de datos gestionadas dentro de la infraestructura en la nube que
          utiliza la plataforma. Las comunicaciones entre tu navegador y FinScope se realizan mediante
          conexiones cifradas (HTTPS/TLS). Las contraseñas de las cuentas se almacenan utilizando funciones de
          hash unidireccional y las credenciales de integraciones externas se almacenan cifradas. La
          información de cada organización está lógicamente separada de la de otras organizaciones.
        </p>
      </LegalSection>

      <LegalSection number={8} title="Retención de la información">
        <p>
          Conservamos la información mientras la cuenta de tu organización permanezca activa o mientras sea
          necesaria para prestar el servicio. Cuando se solicita la eliminación de datos o de una cuenta
          conforme a la sección 16, la información se elimina o anonimiza en un plazo razonable, salvo que
          exista una obligación legítima de conservación.
        </p>
      </LegalSection>

      <LegalSection number={9} title="Compartición de información con terceros">
        <p>
          No vendemos, rentamos ni comercializamos tu información. Solo compartimos información en los
          siguientes supuestos:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Con los proveedores tecnológicos estrictamente necesarios para operar la plataforma (sección 10).</li>
          <li>Con los servicios externos que tú mismo decidas conectar (por ejemplo, Google), en la medida necesaria para operar la integración.</li>
          <li>Cuando exista una obligación legal o un requerimiento válido de autoridad competente.</li>
        </ul>
      </LegalSection>

      <LegalSection number={10} title="Proveedores tecnológicos">
        <p>
          Para operar, FinScope se apoya en proveedores de infraestructura en la nube (alojamiento de la
          aplicación y bases de datos) y, cuando el usuario lo autoriza, en las APIs de servicios que decida
          integrar. Adicionalmente, algunas funcionalidades opcionales de análisis asistido pueden apoyarse en
          proveedores externos de procesamiento; dichas funcionalidades solo procesan la información necesaria
          cuando el usuario las utiliza. Estos proveedores actúan como encargados del tratamiento y no están
          autorizados a usar la información para fines propios.
        </p>
      </LegalSection>

      <LegalSection number={11} title="Seguridad">
        <p>
          Aplicamos medidas de seguridad razonables y proporcionales, entre ellas: autenticación con
          credenciales individuales, control de acceso por roles, aislamiento de la información por
          organización, cifrado de las comunicaciones en tránsito, hash de contraseñas y cifrado de los tokens
          de integraciones. Ningún sistema es infalible, por lo que también dependemos de un uso responsable de
          las credenciales por parte de los usuarios.
        </p>
      </LegalSection>

      <LegalSection number={12} title="Derechos del usuario">
        <p>
          Puedes solicitar el acceso, la rectificación, la actualización o la eliminación de tu información
          personal, así como la revocación de las autorizaciones que hayas otorgado. Para ejercer estos
          derechos, utiliza los canales indicados en la sección de Contacto. Atenderemos las solicitudes en un
          plazo razonable y conforme a la legislación aplicable.
        </p>
      </LegalSection>

      <LegalSection number={13} title="Cookies y tecnologías similares">
        <p>
          FinScope no utiliza cookies de publicidad ni rastreadores de terceros. Para mantener tu sesión
          iniciada, la plataforma utiliza el almacenamiento local del navegador, donde guarda un token de
          sesión y preferencias básicas de uso. Esta información se elimina al cerrar sesión y puedes borrarla
          en cualquier momento desde la configuración de tu navegador.
        </p>
      </LegalSection>

      <LegalSection number={14} title="Cambios a esta política">
        <p>
          Podemos actualizar esta política para reflejar cambios en la plataforma o en la normativa aplicable.
          Publicaremos siempre la versión vigente en esta página, indicando la fecha de última actualización.
          Si los cambios son significativos, lo comunicaremos por medios razonables dentro de la plataforma.
        </p>
      </LegalSection>

      <LegalSection number={15} title="Contacto">
        <p>
          Para cualquier duda sobre esta política o sobre el tratamiento de tu información, contáctanos a
          través de los canales de atención disponibles dentro de la plataforma o mediante el administrador de
          la cuenta de tu organización, quien canalizará tu solicitud con el equipo de FinScope.
        </p>
      </LegalSection>

      <LegalSection number={16} title="Eliminación de datos y cuentas">
        <p>
          Si deseas solicitar la eliminación de tus datos personales o de la cuenta de tu organización,
          presenta la solicitud mediante los canales de contacto descritos en la sección anterior. Además, si
          conectaste una cuenta de Google, puedes desconectarla en cualquier momento desde la sección
          &quot;Conexión de Datos&quot; de la plataforma; al hacerlo, FinScope solicita la revocación del token
          ante Google y elimina las credenciales cifradas almacenadas.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
